import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { AlertTriangle, Check, ExternalLink, Loader2, Plus, Search } from "lucide-react";
import toast from "react-hot-toast";
import { setDocData } from "../../Firebase/Firebase.utils";
import { revalidateNews, searchRss } from "../utils/pressCoverage";
import EmptyState from "./EmptyState";

// Live search tab for /cms/press-coverage. Fires a POST at the
// nextjsprojv1 /api/rss-search proxy, renders the returned items as
// cards, marks anything that already exists in the local `newsRss`
// set with a ✓ Added badge, and lets the agent click Add on the rest.
//
// Adds write client-side via the standard Firestore SDK under the
// authenticated marketing user — same pattern used by the Leader /
// Broker forms. No admin SDK, no cron.
export default function PressCoverageSearch({
  existingHashes,
  onAdded,
}) {
  const currentUser = useSelector((state) => state.userObject);
  const [items, setItems] = useState([]);
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [hideAdded, setHideAdded] = useState(false);
  const [busyHash, setBusyHash] = useState(null);
  // Session-local addition tracker — lets a card flip to ✓ Added
  // instantly without waiting on a Firestore roundtrip / re-fetch.
  const [sessionAdded, setSessionAdded] = useState(() => new Set());

  const runSearch = async () => {
    setLoading(true);
    try {
      const res = await searchRss();
      setItems(Array.isArray(res.items) ? res.items : []);
      setErrors(Array.isArray(res.errors) ? res.errors : []);
      setHasSearched(true);
      if (!res.items?.length) {
        toast("No matches right now — check back later.", { icon: "🔍" });
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Search failed.");
      setItems([]);
      setErrors([]);
      setHasSearched(true);
    } finally {
      setLoading(false);
    }
  };

  const isAdded = (hash) => sessionAdded.has(hash) || existingHashes.has(hash);

  const visibleItems = useMemo(() => {
    if (!hideAdded) return items;
    return items.filter((it) => !isAdded(it.sourceUrlHash));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, hideAdded, existingHashes, sessionAdded]);

  const addOne = async (item) => {
    setBusyHash(item.sourceUrlHash);
    try {
      const nowIso = new Date().toISOString();
      // Manual adds skip the review queue — the human just chose this
      // article, so `status: "approved"` means it shows on /news
      // immediately. Keeping `visible: true` for backwards compat with
      // any client still reading the old field name.
      const payload = {
        sourceUrl: item.sourceUrl,
        title: item.title || "",
        description: item.description || "",
        publisher: item.publisher || "",
        publisherDomain: item.publisherDomain || "",
        publishedAt: item.publishedAt || null,
        coverPhoto: item.coverPhoto || null,
        feedSource: item.feedSource || "",
        status: "approved",
        visible: true,
        source: "manual",
        addedAt: nowIso,
        addedBy: currentUser?.email || null,
        reviewedAt: nowIso,
        reviewedBy: currentUser?.email || null,
        updatedAt: nowIso,
        updatedBy: currentUser?.email || null,
      };
      // Doc id = SHA-1 of the canonical sourceUrl (already computed
      // server-side and echoed as sourceUrlHash). Deterministic id
      // means re-adding is an idempotent set().
      await setDocData("newsRss", item.sourceUrlHash, payload);
      // Optimistic: flip the card + notify the parent so Added tab
      // counts refresh without a full re-fetch.
      setSessionAdded((prev) => {
        const next = new Set(prev);
        next.add(item.sourceUrlHash);
        return next;
      });
      onAdded?.(item.sourceUrlHash);
      toast.success("Added to press coverage.");
      revalidateNews();
    } catch (err) {
      console.error(err);
      toast.error("Failed to add.");
    } finally {
      setBusyHash(null);
    }
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          onClick={runSearch}
          disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-leechiuOrange text-white text-sm font-medium hover:opacity-90 disabled:opacity-60"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Search className="w-4 h-4" />
          )}
          {loading ? "Searching feeds…" : hasSearched ? "Search again" : "Search RSS"}
        </button>
        {hasSearched ? (
          <label className="inline-flex items-center gap-2 text-xs text-gray-600 select-none">
            <input
              type="checkbox"
              checked={hideAdded}
              onChange={(e) => setHideAdded(e.target.checked)}
              className="rounded border-gray-300 text-leechiuBlue focus:ring-leechiuBlue"
            />
            Hide already added
          </label>
        ) : null}
      </div>

      {!hasSearched ? (
        <EmptyState
          Icon={Search}
          title="Search Philippine business press for Leechiu mentions"
          message="Click Search RSS to fetch fresh matches from BusinessWorld, Inquirer Business, PhilStar, and Rappler. Add the ones worth showing on /news."
        />
      ) : errors.length && !items.length ? (
        <EmptyState
          Icon={AlertTriangle}
          title="No matches — and some feeds failed"
          message={`Errors: ${errors.map((e) => e.feed).join(", ")}. Try again in a bit.`}
        />
      ) : items.length === 0 ? (
        <EmptyState
          Icon={Search}
          title="No mentions of Leechiu right now"
          message="The scanned feeds returned no matching stories. Try again in a few hours."
        />
      ) : visibleItems.length === 0 ? (
        <EmptyState
          Icon={Check}
          title="Everything's been added"
          message="Every current match is already in your press coverage feed. Uncheck 'Hide already added' to review them."
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {visibleItems.map((it) => (
            <ResultCard
              key={it.sourceUrlHash}
              item={it}
              added={isAdded(it.sourceUrlHash)}
              busy={busyHash === it.sourceUrlHash}
              onAdd={() => addOne(it)}
            />
          ))}
        </div>
      )}

      {errors.length ? (
        <div className="mt-6 rounded-lg bg-amber-50 border border-amber-100 p-3 text-xs text-amber-900">
          <div className="font-semibold mb-1 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" />
            Some feeds could not be reached
          </div>
          <ul className="list-disc list-inside space-y-0.5">
            {errors.map((e) => (
              <li key={`${e.feed}:${e.reason}`}>
                <span className="font-medium">{e.feed}</span> — {e.reason}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function ResultCard({ item, added, busy, onAdd }) {
  const dateLabel = item.publishedAt
    ? new Date(item.publishedAt).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "";
  return (
    <article
      className={`flex flex-col bg-white rounded-2xl ring-1 ring-gray-100 shadow-sm overflow-hidden transition-opacity ${
        added ? "opacity-70" : ""
      }`}
    >
      <a
        href={item.sourceUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="block aspect-[16/10] bg-gray-100 overflow-hidden"
      >
        {item.coverPhoto ? (
          <img
            src={item.coverPhoto}
            alt=""
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-300 text-xs">
            No image
          </div>
        )}
      </a>
      <div className="p-4 flex flex-col flex-1">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-gray-500">
          <span className="font-semibold text-leechiuOrange">{item.publisher}</span>
          {dateLabel ? <span>· {dateLabel}</span> : null}
        </div>
        <h3 className="mt-2 font-semibold text-sm text-leechiuBlue leading-snug line-clamp-3">
          <a
            href={item.sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-leechiuOrange"
          >
            {item.title}
          </a>
        </h3>
        {item.description ? (
          <p className="mt-2 text-xs text-gray-600 line-clamp-3">
            {item.description}
          </p>
        ) : null}
        <div className="mt-auto pt-4 flex items-center justify-between">
          <a
            href={item.sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-leechiuBlue"
          >
            Source
            <ExternalLink className="w-3 h-3" />
          </a>
          {added ? (
            <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-emerald-50 text-emerald-700 text-xs font-medium ring-1 ring-emerald-200">
              <Check className="w-3.5 h-3.5" />
              Added
            </span>
          ) : (
            <button
              type="button"
              onClick={onAdd}
              disabled={busy}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-leechiuBlue text-white text-xs font-medium hover:opacity-90 disabled:opacity-60"
            >
              {busy ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Plus className="w-3.5 h-3.5" />
              )}
              Add
            </button>
          )}
        </div>
      </div>
    </article>
  );
}
